/**
 * Guscio comune di tutte le pagine di accesso: login dei prodotti, nuova password, ritorno dalla
 * banca. Una scheda centrale con logo, «Mindlead» e nome del prodotto, sullo sfondo sfumato del
 * colore del prodotto; rispetta le aree sicure di notch e home indicator.
 */
import { forwardRef, useState, type ComponentProps, type ReactNode } from "react";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import type { ProductKey } from "@/platform/products";
import { AUTH_BRANDS, type AuthBrand } from "./brand";

const SUPPORT_EMAIL = "info@mindleadsuite.com";

function BrandMark({ brand, size = "md" }: { brand: AuthBrand; size?: "md" | "lg" }) {
  const box = size === "lg" ? "h-14 w-14 rounded-2xl" : "h-11 w-11 rounded-xl";
  if (brand.logo) {
    return <img src={brand.logo} alt="" className={cn(box, "shadow-md shadow-black/10")} />;
  }
  const Icon = brand.icon;
  return (
    <div className={cn(box, brand.accent.iconTile, "flex items-center justify-center shadow-md shadow-black/10")}>
      <Icon className={size === "lg" ? "h-7 w-7" : "h-6 w-6"} />
    </div>
  );
}

export function AuthShell({
  product,
  title,
  subtitle,
  children,
  below,
}: {
  product: ProductKey;
  /** titolo della scheda; se assente il nome del prodotto */
  title?: string;
  subtitle?: string;
  children: ReactNode;
  /** contenuto sotto la scheda (link secondari) */
  below?: ReactNode;
}) {
  const brand = AUTH_BRANDS[product];
  return (
    <div
      className={cn(
        "relative flex min-h-[100dvh] items-center justify-center overflow-hidden px-4 pb-[max(env(safe-area-inset-bottom),1.5rem)] pt-[max(env(safe-area-inset-top),1.5rem)]",
        brand.accent.background,
      )}
    >
      {/* Atmosfera soft di sfondo */}
      <div aria-hidden className={cn("pointer-events-none absolute inset-0", brand.accent.glow)} />

      <div className="relative w-full max-w-[420px]">
        <div
          className={cn(
            "rounded-3xl border bg-white/90 p-8 shadow-xl backdrop-blur-sm sm:p-10",
            brand.accent.card,
          )}
        >
          <div className="mb-8 flex flex-col items-center text-center">
            <div className="mb-5">
              <BrandMark brand={brand} size="lg" />
            </div>
            <p className={cn("text-[11px] font-medium uppercase tracking-[0.2em]", brand.accent.eyebrow)}>
              {title ? `${brand.eyebrow} ${brand.name}` : brand.eyebrow}
            </p>
            <h1 className="mt-1 text-2xl font-semibold tracking-tight text-slate-900 sm:text-[1.65rem]">
              {title ?? brand.name}
            </h1>
            <p className="mt-2 max-w-[300px] text-sm leading-relaxed text-slate-500">{subtitle ?? brand.tagline}</p>
          </div>
          {children}
        </div>

        {below && <div className="mt-6 text-center text-sm text-slate-500">{below}</div>}

        <p className="mt-6 text-center text-xs text-slate-400">
          <a href="/privacy" className="hover:text-slate-600">
            Privacy
          </a>
          <span className="mx-2">·</span>
          <a href={`mailto:${SUPPORT_EMAIL}`} className="hover:text-slate-600">
            Assistenza
          </a>
        </p>
      </div>
    </div>
  );
}

const inputBase = "h-11 rounded-xl border-slate-200 bg-slate-50/80 px-3.5 text-[15px]";

export const AuthField = forwardRef<
  HTMLInputElement,
  ComponentProps<typeof Input> & { label: string; product: ProductKey }
>(function AuthField({ label, product, id, className, ...props }, ref) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id} className="text-slate-700">
        {label}
      </Label>
      <Input ref={ref} id={id} className={cn(inputBase, AUTH_BRANDS[product].accent.input, className)} {...props} />
    </div>
  );
});

export function AuthPasswordField({
  label = "Password",
  product,
  id,
  aside,
  ...props
}: Omit<ComponentProps<typeof Input>, "type"> & { label?: string; product: ProductKey; aside?: ReactNode }) {
  const [visible, setVisible] = useState(false);
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label htmlFor={id} className="text-slate-700">
          {label}
        </Label>
        {aside}
      </div>
      <div className="relative">
        <Input
          id={id}
          type={visible ? "text" : "password"}
          className={cn(inputBase, "pr-11", AUTH_BRANDS[product].accent.input)}
          {...props}
        />
        <button
          type="button"
          onClick={() => setVisible((v) => !v)}
          className="absolute inset-y-0 right-0 flex w-11 items-center justify-center text-slate-400 hover:text-slate-600"
          aria-label={visible ? "Nascondi password" : "Mostra password"}
        >
          {visible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
      </div>
    </div>
  );
}

export function AuthSubmit({
  product,
  loading,
  loadingText,
  children,
  className,
  ...props
}: ComponentProps<typeof Button> & { product: ProductKey; loading?: boolean; loadingText?: string }) {
  return (
    <Button
      type="submit"
      disabled={loading || props.disabled}
      className={cn("h-11 w-full rounded-xl text-[15px] font-medium shadow-sm", AUTH_BRANDS[product].accent.button, className)}
      {...props}
    >
      {loading ? (
        <>
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          {loadingText ?? "Attendere…"}
        </>
      ) : (
        children
      )}
    </Button>
  );
}

/** Messaggio dentro la scheda (errore o avviso). */
export function AuthNotice({ tone = "error", children }: { tone?: "error" | "info"; children: ReactNode }) {
  return (
    <div
      className={cn(
        "mb-5 rounded-xl border px-3.5 py-3 text-sm",
        tone === "error" ? "border-red-200 bg-red-50 text-red-700" : "border-slate-200 bg-slate-50 text-slate-600",
      )}
    >
      {children}
    </div>
  );
}

export function authLinkClass(product: ProductKey) {
  return cn("font-medium", AUTH_BRANDS[product].accent.link);
}
